import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { UserRole } from '@/types';

const routes: RouteRecordRaw[] = [
  // Public routes
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/auth/ResetPasswordView.vue'),
    meta: { requiresAuth: false },
  },
  
  // Main layout with authenticated routes
  {
    path: '/',
    component: () => import('@/layouts/MainLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: '/dashboard',
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: { 
          title: 'Dashboard',
          roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP, UserRole.TEACHER],
        },
      },
      
      // Attendance routes
      {
        path: 'attendance',
        children: [
          {
            path: '',
            name: 'attendance',
            component: () => import('@/views/attendance/AttendanceListView.vue'),
            meta: { 
              title: 'Asistencia',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP, UserRole.TEACHER],
            },
          },
          {
            path: 'interactive',
            name: 'interactive-attendance',
            component: () => import('@/views/attendance/InteractiveAttendanceView.vue'),
            meta: { 
              title: 'Asistencia Interactiva',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP, UserRole.TEACHER],
            },
          },
          {
            path: 'daily/:courseId',
            name: 'daily-attendance',
            component: () => import('@/views/attendance/DailyAttendanceView.vue'),
            meta: { 
              title: 'Asistencia Diaria',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP, UserRole.TEACHER],
            },
          },
          {
            path: 'subject/:courseSubjectId',
            name: 'subject-attendance',
            component: () => import('@/views/attendance/SubjectAttendanceView.vue'),
            meta: { 
              title: 'Asistencia por Asignatura',
              roles: [UserRole.TEACHER],
            },
          },
        ],
      },
      
      // Justifications routes
      {
        path: 'justifications',
        children: [
          {
            path: '',
            name: 'justifications',
            component: () => import('@/views/justifications/JustificationsListView.vue'),
            meta: { 
              title: 'Justificativos',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP, UserRole.TEACHER],
            },
          },
          {
            path: 'review/:id',
            name: 'review-justification',
            component: () => import('@/views/justifications/ReviewJustificationView.vue'),
            meta: { 
              title: 'Revisar Justificativo',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
        ],
      },
      
      // Academic management routes (Admin only)
      {
        path: 'academic',
        children: [
          {
            path: 'courses',
            name: 'courses',
            component: () => import('@/views/academic/CoursesView.vue'),
            meta: { 
              title: 'Cursos',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
          {
            path: 'courses/:id',
            name: 'course-detail',
            component: () => import('@/views/academic/CourseDetailView.vue'),
            meta: { 
              title: 'Detalle del Curso',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
          {
            path: 'students',
            name: 'students',
            component: () => import('@/views/academic/StudentsView.vue'),
            meta: { 
              title: 'Alumnos',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
          {
            path: 'teachers',
            name: 'teachers',
            component: () => import('@/views/academic/TeachersView.vue'),
            meta: { 
              title: 'Docentes',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR],
            },
          },
          {
            path: 'subjects',
            name: 'subjects',
            component: () => import('@/views/academic/SubjectsView.vue'),
            meta: { 
              title: 'Asignaturas',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
        ],
      },
      
      // Reports routes
      {
        path: 'reports',
        children: [
          {
            path: '',
            name: 'reports',
            component: () => import('@/views/reports/ReportsView.vue'),
            meta: { 
              title: 'Reportes',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
          {
            path: 'monthly',
            name: 'monthly-report',
            component: () => import('@/views/reports/MonthlyReportView.vue'),
            meta: { 
              title: 'Reporte Mensual',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
            },
          },
          {
            path: 'sige',
            name: 'sige-export',
            component: () => import('@/views/reports/SigeExportView.vue'),
            meta: { 
              title: 'Exportación SIGE',
              roles: [UserRole.ADMIN, UserRole.DIRECTOR],
            },
          },
        ],
      },
      
      // Administración de establecimientos (super admin)
      {
        path: 'tenants',
        name: 'tenants-admin',
        component: () => import('@/views/admin/TenantsView.vue'),
        meta: {
          title: 'Establecimientos',
          roles: [UserRole.SUPER_ADMIN],
        },
      },

      // Auditoría
      {
        path: 'audit',
        name: 'audit',
        component: () => import('@/views/admin/AuditView.vue'),
        meta: {
          title: 'Auditoría',
          roles: [UserRole.SUPER_ADMIN, UserRole.ADMIN, UserRole.DIRECTOR],
        },
      },

      // Settings
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: { 
          title: 'Configuración',
          roles: [UserRole.ADMIN, UserRole.DIRECTOR],
        },
      },
      
      // Profile
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/profile/ProfileView.vue'),
        meta: { 
          title: 'Mi Perfil',
        },
      },

      // Calendario escolar
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('@/views/academic/CalendarView.vue'),
        meta: {
          title: 'Calendario Escolar',
          roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
        },
      },

      // Datos SIGE de un estudiante
      {
        path: 'students/:id/sige',
        name: 'student-sige',
        component: () => import('@/views/academic/StudentSigeView.vue'),
        meta: {
          title: 'Datos SIGE del estudiante',
          roles: [UserRole.ADMIN, UserRole.DIRECTOR, UserRole.UTP],
        },
      },

      // Acceso denegado
      {
        path: 'forbidden',
        name: 'forbidden',
        component: () => import('@/views/errors/ForbiddenView.vue'),
        meta: { title: 'Acceso denegado' },
      },
    ],
  },
  
  // Guardian portal
  {
    path: '/guardian',
    component: () => import('@/layouts/GuardianLayout.vue'),
    meta: { requiresAuth: true, roles: [UserRole.GUARDIAN] },
    children: [
      {
        path: '',
        redirect: '/guardian/attendance',
      },
      {
        path: 'attendance',
        name: 'guardian-attendance',
        component: () => import('@/views/guardian/AttendanceView.vue'),
        meta: { title: 'Asistencia de mi Pupilo' },
      },
      {
        path: 'justifications',
        name: 'guardian-justifications',
        component: () => import('@/views/guardian/JustificationsView.vue'),
        meta: { title: 'Mis Justificativos' },
      },
      {
        path: 'justifications/new',
        name: 'guardian-new-justification',
        component: () => import('@/views/guardian/NewJustificationView.vue'),
        meta: { title: 'Nuevo Justificativo' },
      },
    ],
  },
  
  // 404
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/errors/NotFoundView.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guard
router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore();
  
  // Check if route requires authentication
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth !== false);
  
  if (requiresAuth && !authStore.isAuthenticated) {
    // Not authenticated, redirect to login
    next({ name: 'login', query: { redirect: to.fullPath } });
    return;
  }
  
  if (to.name === 'login' && authStore.isAuthenticated) {
    // Already authenticated, redirect based on role
    if (authStore.userRole === UserRole.GUARDIAN) {
      next({ name: 'guardian-attendance' });
    } else {
      next({ name: 'dashboard' });
    }
    return;
  }
  
  // Check role-based access
  const allowedRoles = to.meta.roles as UserRole[] | undefined;
  if (allowedRoles && allowedRoles.length > 0) {
    if (!authStore.hasRole(allowedRoles)) {
      // El usuario no tiene el rol requerido
      next({ name: 'forbidden' });
      return;
    }
  }
  
  next();
});

// Update document title
router.afterEach((to) => {
  const title = to.meta.title as string | undefined;
  const appTitle = import.meta.env.VITE_APP_TITLE || 'Sistema de Asistencia';
  document.title = title ? `${title} | ${appTitle}` : appTitle;
});

export default router;
